import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ModernCard from '../components/ModernCard';
import ModernButton from '../components/ModernButton';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ProfileService, ProfileData, PrivacySettings } from '../services/profileService';
import { supabase } from '../lib/supabase';
import { oauthConfig } from '../lib/oauth-config';
import { SkillsService, Skill } from '../services/skillsService';
import { AwardsService, Award } from '../services/awardsService';
import { ProjectsService, Project } from '../services/projectsService';
import { showSuccessNotification, showErrorNotification } from '../utils/notifications';
import SkillModal from '../components/SkillModal';
import AwardModal from '../components/AwardModal';
import ProjectCard from '../components/ProjectCard';
import { SubscriptionDebugPanel } from '../components/SubscriptionDebugPanel';
import { useStripeSubscription } from '../hooks/useStripeSubscription';
import { useSubscription as useSubscriptionContext } from '../contexts/SubscriptionContext';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';
import { usePauseSubscription } from '../hooks/usePauseSubscription';

// ErrorBoundary pour PageSettings
class SettingsErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[Settings ErrorBoundary] Error:', error);
    console.error('[Settings ErrorBoundary] ErrorInfo:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen theme-layout-bg theme-text-primary p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Settings</h1>
            <ModernCard>
              <div className="p-6 text-center">
                <p className="text-red-400 mb-4">An error occurred while loading settings.</p>
                <ModernButton onClick={() => window.location.reload()}>
                  Reload Page
                </ModernButton>
              </div>
            </ModernCard>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

import { 
  User, 
  Shield, 
  Bell, 
  Palette, 
  Globe, 
  Mail, 
  Phone, 
  Eye, 
  Save,
  X,
  Loader2,
  ChevronRight,
  ExternalLink,
  Link as LinkIcon,
  Award as AwardIcon,
  Plus,
  Edit,
  CreditCard,
  FolderKanban,
  Crown,
  Clock
} from 'lucide-react';

interface SettingsCategory {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  description: string;
}

const PageSettingsContent: React.FC = () => {
  const { user } = useAuth();
  const { currentTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState<string>('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Subscription management - Use multiple sources for complete data
  const { subscription: stripeSubscription, loading: subscriptionLoading, refresh: refreshSubscription } = useStripeSubscription(); // ✅ Utilise refresh() standardisé
  const { subscription: permissionsSubscription, loading: permissionsLoading } = useSubscriptionPermissions();
  const { openBillingPortal, cancelSubscription } = useSubscriptionContext();
  const { pauseSubscription, resumeSubscription, loading: pauseLoading } = usePauseSubscription();
  const [isCancelling, setIsCancelling] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  
  // Combine subscription data: prefer Stripe data if available, fallback to permissions data
  const currentSubscription = stripeSubscription || (permissionsSubscription ? {
    customer_id: '',
    subscription_id: null,
    subscription_status: permissionsSubscription.status || 'active',
    price_id: null,
    current_period_start: null,
    current_period_end: permissionsSubscription.end_date ? Math.floor(new Date(permissionsSubscription.end_date).getTime() / 1000) : null,
    cancel_at_period_end: false,
    payment_method_brand: null,
    payment_method_last4: null,
    product_name: permissionsSubscription.plan_display_name || permissionsSubscription.plan_name,
    product_description: ''
  } : null);
  
  const isLoadingSubscription = subscriptionLoading || permissionsLoading;
  
  // Debug logs (dev only)
  if (import.meta.env.DEV && activeCategory === 'subscription') {
    console.log('[Settings Subscription Debug]', {
      stripeSubscription,
      permissionsSubscription,
      currentSubscription,
      subscriptionLoading,
      permissionsLoading,
      isLoadingSubscription,
      hasStripe: !!stripeSubscription,
      hasPermissions: !!permissionsSubscription,
      planName: permissionsSubscription?.plan_name,
      planDisplayName: permissionsSubscription?.plan_display_name
    });
  }
  
  // OAuth identities
  const [discordConnected, setDiscordConnected] = useState(false);
  const [discordUsername, setDiscordUsername] = useState<string | null>(null);
  const [githubConnected, setGithubConnected] = useState(false);
  const [githubUsername, setGithubUsername] = useState<string | null>(null);
  
  // Profile data
  const [profileData, setProfileData] = useState<ProfileData>({
    full_name: '',
    professional_title: '',
    location: '',
    bio: '',
    website: '',
    email: user?.email || '',
    phone: '',
    contact_email: '',
    contact_phone: '',
    status: 'available',
    show_email: true,
    show_phone: true
  });

  // Privacy settings
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    show_email: true,
    show_phone: true,
    is_profile_public: true
  });

  // Social networks
  const [socialNetworks, setSocialNetworks] = useState({
    github: '',
    linkedin: '',
    twitter: '',
    discord: '',
    instagram: '',
    tiktok: '',
    youtube: '',
    website: ''
  });

  // Skills, awards et projets
  const [skills, setSkills] = useState<Skill[]>([]);
  const [awards, setAwards] = useState<Award[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isEditingSkill, setIsEditingSkill] = useState(false);
  const [isEditingAward, setIsEditingAward] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Skill | null>(null);
  const [editingAward, setEditingAward] = useState<Award | null>(null);
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Settings categories - définies directement sans useMemo pour éviter les problèmes avec les icônes
  const categories: SettingsCategory[] = [
    {
      id: 'profile',
      name: 'Profile',
      icon: User,
      description: 'Personal and professional information'
    },
    {
      id: 'privacy',
      name: 'Privacy',
      icon: Shield,
      description: 'Privacy and visibility settings'
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: Bell,
      description: 'Notification preferences'
    },
    {
      id: 'appearance',
      name: 'Appearance',
      icon: Palette,
      description: 'Theme and visual preferences'
    },
    {
      id: 'social',
      name: 'Social Networks',
      icon: Globe,
      description: 'Links to your social networks'
    },
    {
      id: 'skills',
      name: 'Skills & Awards',
      icon: AwardIcon,
      description: 'Manage your skills and awards'
    },
    {
      id: 'projects',
      name: 'Projects',
      icon: FolderKanban,
      description: 'Manage your public projects'
    },
    {
      id: 'subscription',
      name: 'Subscription & Payment',
      icon: CreditCard,
      description: 'Manage your subscription and payment methods'
    }
  ];

  // Handle URL parameters
  useEffect(() => {
    const category = searchParams.get('category');
    const validCategories = ['profile', 'privacy', 'notifications', 'appearance', 'social', 'skills', 'projects', 'subscription'];
    if (category && validCategories.includes(category)) {
      setActiveCategory(category);
    }
  }, [searchParams]);

  // Load OAuth identities
  useEffect(() => {
    const loadOAuthIdentities = async () => {
      if (!user || !supabase) return;

      try {
        // Récupérer les identités de l'utilisateur
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        if (authUser?.identities) {
          // Vérifier Discord
          const discordIdentity = authUser.identities.find(id => id.provider === 'discord');
          if (discordIdentity) {
            setDiscordConnected(true);
            // Récupérer le username depuis les metadata
            const discordUsername = authUser.user_metadata?.discord_username || 
                                  authUser.user_metadata?.preferred_username ||
                                  authUser.user_metadata?.full_name ||
                                  discordIdentity.identity_data?.preferred_username ||
                                  discordIdentity.identity_data?.full_name ||
                                  'Discord User';
            setDiscordUsername(discordUsername);
          } else {
            setDiscordConnected(false);
            setDiscordUsername(null);
          }

          // Vérifier GitHub
          const githubIdentity = authUser.identities.find(id => id.provider === 'github');
          if (githubIdentity) {
            setGithubConnected(true);
            // Récupérer le username depuis les metadata
            const githubUsername = authUser.user_metadata?.user_name ||
                                 authUser.user_metadata?.preferred_username ||
                                 githubIdentity.identity_data?.user_name ||
                                 githubIdentity.identity_data?.preferred_username ||
                                 githubIdentity.identity_data?.login ||
                                 'GitHub User';
            setGithubUsername(githubUsername);
          } else {
            setGithubConnected(false);
            setGithubUsername(null);
          }
        }
      } catch (error) {
        console.error('Error loading OAuth identities:', error);
      }
    };

    loadOAuthIdentities();

    // Écouter les changements d'auth state pour recharger après connexion OAuth
    if (supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
        if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
          loadOAuthIdentities();
        }
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [user]);

  // Load profile data
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // Load profile
        const data = await ProfileService.getProfile(user.id);
        
        if (data) {
          setProfileData(prev => ({
            ...prev,
            ...data,
            email: data.email || user.email || ''
          }));
          
          setPrivacySettings({
            show_email: data.show_email ?? true,
            show_phone: data.show_phone ?? true,
            is_profile_public: data.is_profile_public !== false
          });

          setSocialNetworks({
            github: '', // GitHub ne sera plus modifiable, donc vide ici
            linkedin: data.linkedin_url || '',
            twitter: data.twitter_url || '',
            discord: '', // Discord ne sera plus modifiable, donc vide ici
            instagram: (data as any).instagram_url || '',
            tiktok: (data as any).tiktok_url || '',
            youtube: (data as any).youtube_url || '',
            website: data.website || ''
          });

          // Load theme - Synchroniser avec ThemeContext si disponible
          if ((data as any).theme && ['dark', 'light'].includes((data as any).theme)) {
            setTheme((data as any).theme as 'dark' | 'light');
          }
        }

        // Load skills
        try {
          const userSkills = await SkillsService.getUserSkills(user.id);
          setSkills(Array.isArray(userSkills) ? userSkills : []);
        } catch (error) {
          console.error('Error loading skills:', error);
          setSkills([]);
        }

        // Load awards
        try {
          const userAwards = await AwardsService.getUserAwards(user.id);
          setAwards(Array.isArray(userAwards) ? userAwards : []);
        } catch (error) {
          console.error('Error loading awards:', error);
          setAwards([]);
        }

        // Load projects
        try {
          const userProjects = await ProjectsService.getUserProjects(user.id);
          setProjects(Array.isArray(userProjects) ? userProjects : []);
        } catch (error) {
          console.error('Error loading projects:', error);
          setProjects([]);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        showErrorNotification('Failed to load settings');
        // Assurer que les états sont initialisés même en cas d'erreur
        setSkills([]);
        setAwards([]);
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfileData();
  }, [user]);

  // Save theme - Synchronisé avec ThemeContext
  const handleThemeChange = async (theme: 'dark' | 'light') => {
    if (!user) return;
    
    // Mettre à jour le thème via ThemeContext (qui synchronise avec localStorage et la DB)
    setTheme(theme);
    
    try {
      await ProfileService.updateProfile(user.id, { theme } as any);
      showSuccessNotification('Theme saved!');
    } catch (error) {
      console.error('Error saving theme:', error);
      showErrorNotification('Error saving theme');
    }
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setIsSaving(true);
    try {
      const dataToSave: any = {
        ...profileData,
        // GitHub et Discord ne sont plus sauvegardés depuis les inputs car ils viennent de OAuth
        github_url: githubUsername || null, // Utiliser le username OAuth si disponible
        linkedin_url: socialNetworks.linkedin,
        twitter_url: socialNetworks.twitter,
        discord_username: discordUsername || null, // Utiliser le username OAuth si disponible
        instagram_url: socialNetworks.instagram,
        tiktok_url: socialNetworks.tiktok,
        youtube_url: socialNetworks.youtube,
        website: socialNetworks.website
      };

      const success = await ProfileService.updateProfile(user.id, dataToSave);
      const privacySuccess = await ProfileService.updatePrivacySettings(user.id, privacySettings);
      
      if (success && privacySuccess) {
        showSuccessNotification('Settings saved successfully!');
        
        // Reload profile data
        try {
          const updatedData = await ProfileService.getProfile(user.id);
          if (updatedData) {
            setProfileData(prev => ({ ...prev, ...updatedData }));
            setSocialNetworks({
              github: '', // GitHub géré via OAuth
              linkedin: updatedData.linkedin_url || '',
              twitter: updatedData.twitter_url || '',
              discord: '', // Discord géré via OAuth
              instagram: (updatedData as any).instagram_url || '',
              tiktok: (updatedData as any).tiktok_url || '',
              youtube: (updatedData as any).youtube_url || '',
              website: updatedData.website || ''
            });
          }
        } catch (error) {
          console.error('Error reloading profile:', error);
        }
      } else {
        showErrorNotification('Error saving settings');
      }
    } catch (error) {
      console.error('Error saving:', error);
      showErrorNotification('An error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  };

  // Render category content
  const renderCategoryContent = () => {
    try {
      switch (activeCategory) {
      case 'profile':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold theme-text-primary mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={profileData.full_name || ''}
                    onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Professional Title
                  </label>
                  <input
                    type="text"
                    value={profileData.professional_title || ''}
                    onChange={(e) => setProfileData({...profileData, professional_title: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={profileData.location || ''}
                    onChange={(e) => setProfileData({...profileData, location: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={profileData.website || ''}
                    onChange={(e) => setProfileData({...profileData, website: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                    placeholder="https://your-website.com"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="block text-sm font-medium theme-text-secondary mb-2">
                  Bio
                </label>
                <textarea
                  value={profileData.bio || ''}
                  onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2] resize-none"
                  placeholder="Tell us about yourself..."
                />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold theme-text-primary mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={profileData.contact_email || ''}
                    onChange={(e) => setProfileData({...profileData, contact_email: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                    placeholder="contact@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={profileData.contact_phone || ''}
                    onChange={(e) => setProfileData({...profileData, contact_phone: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                    placeholder="+33 6 12 34 56 78"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold theme-text-primary mb-4">Information Visibility</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 theme-card-bg theme-card-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Eye size={20} className="theme-text-secondary" />
                    <div>
                      <div className="text-sm font-medium theme-text-primary">Public profile</div>
                      <div className="text-xs theme-text-secondary">
                        Allow everyone to view your profile page
                      </div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacySettings.is_profile_public !== false}
                      onChange={(e) =>
                        setPrivacySettings({
                          ...privacySettings,
                          is_profile_public: e.target.checked
                        })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#9c68f2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#9c68f2]"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 theme-card-bg theme-card-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="theme-text-secondary" />
                    <div>
                      <div className="text-sm font-medium theme-text-primary">Show email</div>
                      <div className="text-xs theme-text-secondary">Allow other users to see your email</div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacySettings.show_email}
                      onChange={(e) => setPrivacySettings({...privacySettings, show_email: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#9c68f2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#9c68f2]"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 theme-card-bg theme-card-border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="theme-text-secondary" />
                    <div>
                      <div className="text-sm font-medium theme-text-primary">Show phone</div>
                      <div className="text-xs theme-text-secondary">Allow other users to see your phone</div>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacySettings.show_phone}
                      onChange={(e) => setPrivacySettings({...privacySettings, show_phone: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#9c68f2]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#9c68f2]"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'social':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold theme-text-primary mb-4">Social Networks</h3>
              <p className="theme-text-secondary text-sm mb-6">Enter your username (without @ or https://), links will be generated automatically</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* GitHub - Non modifiable, connecté via OAuth */}
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    GitHub
                  </label>
                  {githubConnected && githubUsername ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 px-3 py-2 theme-card-bg theme-card-border rounded-lg">
                        <span className="theme-text-primary flex-1">{githubUsername}</span>
                        <a
                          href={`https://github.com/${githubUsername}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#9c68f2] hover:text-[#8a5ae8] transition-colors"
                          title="View GitHub profile"
                        >
                          <ExternalLink size={16} />
                        </a>
                      </div>
                      <p className="text-xs theme-text-secondary">Connected via OAuth - github.com/{githubUsername}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="px-3 py-2 theme-card-bg theme-card-border rounded-lg theme-text-secondary">
                        Not connected
                      </div>
                      <p className="text-xs theme-text-secondary">Connect your GitHub account to display it on your profile</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    LinkedIn
                  </label>
                  <input
                    type="text"
                    placeholder="username"
                    value={socialNetworks.linkedin}
                    onChange={(e) => setSocialNetworks({...socialNetworks, linkedin: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                  <p className="text-xs theme-text-secondary mt-1">linkedin.com/in/{socialNetworks.linkedin || 'your-name'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Twitter (X)
                  </label>
                  <input
                    type="text"
                    placeholder="username"
                    value={socialNetworks.twitter}
                    onChange={(e) => setSocialNetworks({...socialNetworks, twitter: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                  <p className="text-xs theme-text-secondary mt-1">twitter.com/{socialNetworks.twitter || 'your-name'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Instagram
                  </label>
                  <input
                    type="text"
                    placeholder="username"
                    value={socialNetworks.instagram}
                    onChange={(e) => setSocialNetworks({...socialNetworks, instagram: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                  <p className="text-xs theme-text-secondary mt-1">instagram.com/{socialNetworks.instagram || 'your-name'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    TikTok
                  </label>
                  <input
                    type="text"
                    placeholder="username"
                    value={socialNetworks.tiktok}
                    onChange={(e) => setSocialNetworks({...socialNetworks, tiktok: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                  <p className="text-xs theme-text-secondary mt-1">tiktok.com/@{socialNetworks.tiktok || 'your-name'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    YouTube
                  </label>
                  <input
                    type="text"
                    placeholder="channel name"
                    value={socialNetworks.youtube}
                    onChange={(e) => setSocialNetworks({...socialNetworks, youtube: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                  <p className="text-xs theme-text-secondary mt-1">youtube.com/c/{socialNetworks.youtube || 'your-channel'}</p>
                </div>

                {/* Discord - Non modifiable, connecté via OAuth */}
                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Discord
                  </label>
                  {discordConnected && discordUsername ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 px-3 py-2 theme-card-bg theme-card-border rounded-lg">
                        <span className="theme-text-primary flex-1">{discordUsername}</span>
                      </div>
                      <p className="text-xs theme-text-secondary">Connected via OAuth</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        onClick={async () => {
                          if (!supabase) {
                            showErrorNotification('Supabase not configured');
                            return;
                          }
                          try {
                            const { error } = await supabase.auth.signInWithOAuth({
                              provider: 'discord',
                              options: {
                                ...oauthConfig,
                                redirectTo: `${window.location.origin}/auth/callback?redirect=/settings?category=social`
                              }
                            });
                            if (error) {
                              showErrorNotification('Failed to connect Discord');
                            }
                          } catch (error) {
                            console.error('Error connecting Discord:', error);
                            showErrorNotification('Failed to connect Discord');
                          }
                        }}
                        className="w-full px-4 py-2 bg-[#5865F2] hover:bg-[#4752c4] text-[rgb(var(--fg))] rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <LinkIcon size={16} />
                        Connect Discord
                      </button>
                      <p className="text-xs theme-text-secondary">Connect your Discord account to display it on your profile</p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium theme-text-secondary mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    placeholder="https://mywebsite.com"
                    value={socialNetworks.website}
                    onChange={(e) => setSocialNetworks({...socialNetworks, website: e.target.value})}
                    className="w-full px-3 py-2 theme-card-bg border border-gray-300 dark:border-gray-600 rounded-lg theme-text-primary placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#9c68f2] focus:border-[#9c68f2]"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold theme-text-primary mb-4">Notification Preferences</h3>
              <div className="text-center py-12">
                <Bell size={48} className="mx-auto theme-text-secondary mb-4 opacity-50" />
                <h3 className="text-lg font-semibold theme-text-primary mb-2">Notifications</h3>
                <p className="theme-text-secondary">Notification settings will be available soon</p>
              </div>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold theme-text-primary mb-4">Appearance Preferences</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="theme-card-bg theme-card-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="theme-text-primary font-semibold">Dark Theme</h4>
                    <div className="w-4 h-4 bg-gray-800 rounded-full border-2 border-gray-600"></div>
                  </div>
                  <p className="theme-text-secondary text-sm mb-3">Classic dark interface</p>
                  <button 
                    onClick={() => handleThemeChange('dark')}
                    className="w-full py-2 rounded-lg transition-colors text-[rgb(var(--fg))] hover:opacity-80 bg-[#9c68f2]"
                  >
                    {currentTheme === 'dark' ? 'Active' : 'Activate'}
                  </button>
                </div>

                <div className="theme-card-bg theme-card-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="theme-text-primary font-semibold">Light Theme</h4>
                    <div className="w-4 h-4 bg-white rounded-full border-2 border-gray-300"></div>
                  </div>
                  <p className="theme-text-secondary text-sm mb-3">Clean and modern interface</p>
                  <button 
                    onClick={() => handleThemeChange('light')}
                    className="w-full py-2 rounded-lg transition-colors text-[rgb(var(--fg))] hover:opacity-80 bg-[#9c68f2]"
                  >
                    {currentTheme === 'light' ? 'Active' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );

      case 'skills':
        return (
          <div className="space-y-8">
            <ModernCard>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold theme-text-primary">Skills</h3>
                    <p className="theme-text-secondary text-sm">Manage your skills and levels</p>
                  </div>
                  <ModernButton
                    onClick={() => {
                      setEditingSkill(null);
                      setIsEditingSkill(true);
                    }}
                  >
                    <Plus size={16} className="mr-2" />
                    Add Skill
                  </ModernButton>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {skills && skills.length > 0 && skills.map((skill) => (
                    <div key={skill.id} className="theme-card-bg theme-card-border rounded-lg p-4 hover:border-[#9c68f2]/50 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="theme-text-primary font-medium mb-1">{skill.name}</h4>
                          <p className="text-sm theme-text-secondary mb-2">{skill.category}</p>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                            skill.level === 'expert' ? 'bg-orange-500/20 text-orange-400' :
                            skill.level === 'advanced' ? 'bg-purple-500/20 text-purple-400' :
                            skill.level === 'intermediate' ? 'bg-blue-500/20 text-[rgb(var(--brand))]' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {skill.level === 'expert' ? 'Expert' :
                             skill.level === 'advanced' ? 'Advanced' :
                             skill.level === 'intermediate' ? 'Intermediate' : 'Beginner'}
                          </span>
                        </div>
                        <div className="flex gap-1 ml-2">
                          <button
                            onClick={() => {
                              setEditingSkill(skill);
                              setIsEditingSkill(true);
                            }}
                            className="p-1 hover:theme-hover-button rounded transition-colors"
                          >
                            <Edit size={14} className="theme-text-secondary" />
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                if (await SkillsService.deleteSkill(skill.id)) {
                                  setSkills(skills.filter(s => s.id !== skill.id));
                                  showSuccessNotification('Skill deleted');
                                }
                              } catch (error) {
                                console.error('Error deleting skill:', error);
                                showErrorNotification('Error deleting skill');
                              }
                            }}
                            className="p-1 hover:theme-hover-button rounded transition-colors"
                          >
                            <X size={14} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!skills || skills.length === 0) && (
                    <div className="col-span-full text-center py-12 theme-text-secondary">
                      <AwardIcon size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No skills</p>
                      <p className="text-sm">Add your first skills</p>
                    </div>
                  )}
                </div>
              </div>
            </ModernCard>

            <ModernCard>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold theme-text-primary">Awards</h3>
                    <p className="theme-text-secondary text-sm">Manage your awards and certifications</p>
                  </div>
                  <ModernButton
                    onClick={() => {
                      setEditingAward(null);
                      setIsEditingAward(true);
                    }}
                  >
                    <Plus size={16} className="mr-2" />
                    Add Award
                  </ModernButton>
                </div>
                
                <div className="space-y-4">
                  {awards && awards.length > 0 && awards.map((award) => (
                    <div key={award.id} className="theme-card-bg theme-card-border rounded-lg p-4 hover:border-[#9c68f2]/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-start gap-3">
                            <AwardIcon size={20} className="text-yellow-400 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="theme-text-primary font-medium mb-1">{award.title}</h4>
                              <p className="text-sm theme-text-secondary mb-2">{award.issuer}</p>
                              <div className="flex items-center gap-3 text-xs theme-text-secondary">
                                <span>{award.category}</span>
                                <span>•</span>
                                <span>{new Date(award.date_received).toLocaleDateString('en-US')}</span>
                              </div>
                              {award.description && (
                                <p className="text-sm theme-text-secondary mt-2">{award.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1 ml-4">
                          <button
                            onClick={() => {
                              setEditingAward(award);
                              setIsEditingAward(true);
                            }}
                            className="p-1 hover:theme-hover-button rounded transition-colors"
                          >
                            <Edit size={14} className="theme-text-secondary" />
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                if (await AwardsService.deleteAward(award.id)) {
                                  setAwards(awards.filter(a => a.id !== award.id));
                                  showSuccessNotification('Award deleted');
                                }
                              } catch (error) {
                                console.error('Error deleting award:', error);
                                showErrorNotification('Error deleting award');
                              }
                            }}
                            className="p-1 hover:theme-hover-button rounded transition-colors"
                          >
                            <X size={14} className="text-red-400" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!awards || awards.length === 0) && (
                    <div className="text-center py-12 theme-text-secondary">
                      <AwardIcon size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No awards</p>
                      <p className="text-sm">Add your first awards</p>
                    </div>
                  )}
                </div>
              </div>
            </ModernCard>
          </div>
        );

      case 'projects':
        return (
          <div className="space-y-8">
            <ModernCard>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-semibold theme-text-primary">Projects</h3>
                    <p className="theme-text-secondary text-sm">Manage your projects and make them public</p>
                  </div>
                  <ModernButton
                    onClick={() => {
                      showErrorNotification('Project creation page coming soon');
                    }}
                  >
                    <Plus size={16} className="mr-2" />
                    New Project
                  </ModernButton>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects && Array.isArray(projects) && projects.length > 0 && projects.map((project) => {
                    if (!project || !project.id) {
                      return null;
                    }
                    try {
                      return (
                        <div key={project.id} className="relative">
                          <ProjectCard
                            project={project}
                            showAuthor={false}
                            onView={() => navigate(`/project/${project.id}`)}
                          />
                          <div className="absolute top-2 right-2 flex gap-2 z-10">
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              try {
                                await ProjectsService.updateProject(project.id, { is_public: !project.is_public });
                                setProjects(projects.map(p => 
                                  p.id === project.id ? { ...p, is_public: !p.is_public } : p
                                ));
                                showSuccessNotification(
                                  project.is_public ? 'Project made private' : 'Project made public'
                                );
                              } catch (error) {
                                console.error('Error updating project:', error);
                                showErrorNotification('Error updating project');
                              }
                            }}
                            className={`p-2 rounded-lg transition-colors ${
                              project.is_public
                                ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                                : 'bg-gray-500/20 text-[rgb(var(--muted))] hover:bg-gray-500/30'
                            }`}
                            title={project.is_public ? 'Make private' : 'Make public'}
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (window.confirm('Are you sure you want to delete this project?')) {
                                try {
                                  await ProjectsService.deleteProject(project.id);
                                  setProjects(projects.filter(p => p.id !== project.id));
                                  showSuccessNotification('Project deleted');
                                } catch (error) {
                                  console.error('Error deleting project:', error);
                                  showErrorNotification('Error deleting project');
                                }
                              }
                            }}
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                            title="Delete project"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                      );
                    } catch (error) {
                      console.error('Error rendering project card:', error, project);
                      return null;
                    }
                  })}
                  
                  {(!projects || projects.length === 0) && (
                    <div className="col-span-full text-center py-12 theme-text-secondary">
                      <FolderKanban size={48} className="mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No projects</p>
                      <p className="text-sm mb-4">Create your first project to showcase your work</p>
                      <ModernButton onClick={() => {
                        showErrorNotification('Project creation page coming soon');
                      }}>
                        <Plus size={16} className="mr-2" />
                        Create Project
                      </ModernButton>
                    </div>
                  )}
                </div>
              </div>
            </ModernCard>
          </div>
        );

      case 'subscription':
        // Check if user has any subscription data (premium plan or Stripe subscription)
        const hasPremiumPlan = permissionsSubscription && permissionsSubscription.plan_name && 
                               permissionsSubscription.plan_name !== 'launch' && 
                               permissionsSubscription.plan_name !== 'free';
        const hasActiveStripeSubscription = currentSubscription && 
                                            currentSubscription.subscription_status !== 'canceled' && 
                                            currentSubscription.subscription_status !== 'inactive';
        const shouldShowSubscriptionDetails = hasActiveStripeSubscription || hasPremiumPlan;
        
        // Helper function to format dates safely
        const formatDate = (dateValue: number | string | null | undefined): string => {
          if (!dateValue) return 'N/A';
          try {
            const date = typeof dateValue === 'number' 
              ? new Date(dateValue * 1000) 
              : new Date(dateValue);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          } catch {
            return 'Invalid Date';
          }
        };
        
        return (
          <div className="space-y-6">
            {/* Current Subscription */}
            {isLoadingSubscription ? (
              <ModernCard>
                <div className="p-8 text-center">
                  <Loader2 size={32} className="mx-auto text-[#9c68f2] animate-spin mb-4" />
                  <p className="theme-text-secondary">Loading subscription information...</p>
                </div>
              </ModernCard>
            ) : shouldShowSubscriptionDetails ? (
              <>
                <ModernCard className="overflow-hidden">
                  <div className="p-6 sm:p-8">
                    {/* Header */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8 pb-6 border-b theme-card-border">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#9c68f2] to-[#422ca5] rounded-xl flex items-center justify-center shadow-lg">
                          <Crown size={28} className="text-white" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold theme-text-primary mb-1">Current Subscription</h3>
                          <p className="theme-text-secondary text-sm">Manage your subscription and payment methods</p>
                        </div>
                      </div>
                    </div>

                    {/* Plan Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      {/* Plan */}
                      <div className="p-5 theme-card-bg theme-card-border rounded-xl border-2">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium theme-text-secondary uppercase tracking-wide">Plan</span>
                        </div>
                        <div className="mb-2">
                          <span className="text-2xl font-bold theme-text-primary">
                            {currentSubscription.product_name || permissionsSubscription?.plan_display_name || 'Unknown Plan'}
                          </span>
                        </div>
                        {currentSubscription.product_description && (
                          <p className="text-sm theme-text-secondary mt-2">{currentSubscription.product_description}</p>
                        )}
                        {permissionsSubscription?.plan_name && permissionsSubscription.plan_name !== 'launch' && (
                          <p className="text-sm theme-text-secondary mt-2">
                            {permissionsSubscription.plan_name === 'boost' ? 'Boost features with monthly billing' :
                             permissionsSubscription.plan_name === 'scale' ? 'Scale features with monthly billing' : ''}
                          </p>
                        )}
                      </div>

                      {/* Status */}
                      <div className="p-5 theme-card-bg theme-card-border rounded-xl border-2">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium theme-text-secondary uppercase tracking-wide">Status</span>
                        </div>
                        <div className="mb-2">
                          <span className={`inline-flex px-4 py-2 rounded-lg text-sm font-semibold ${
                            currentSubscription.subscription_status === 'active' 
                              ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                              : currentSubscription.subscription_status === 'trialing'
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                              : currentSubscription.subscription_status === 'past_due'
                              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                          }`}>
                            {currentSubscription.subscription_status === 'active' ? '✓ Active' :
                             currentSubscription.subscription_status === 'trialing' ? '⏱ Trial' :
                             currentSubscription.subscription_status === 'past_due' ? '⚠ Past Due' :
                             currentSubscription.subscription_status || 'Active'}
                          </span>
                        </div>
                        {currentSubscription.cancel_at_period_end && (
                          <p className="text-sm text-yellow-400 mt-3 flex items-center gap-2">
                            <span>⚠️</span>
                            <span>Will be cancelled at period end</span>
                          </p>
                        )}
                      </div>

                      {/* Billing Period */}
                      {(currentSubscription.current_period_end || permissionsSubscription?.end_date) && (
                        <div className="p-5 theme-card-bg theme-card-border rounded-xl border-2">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium theme-text-secondary uppercase tracking-wide">
                              {currentSubscription.cancel_at_period_end ? 'Expires on' : 'Next billing'}
                            </span>
                          </div>
                          <div className="mb-2">
                            <span className="text-xl font-bold theme-text-primary">
                              {formatDate(currentSubscription.current_period_end || permissionsSubscription?.end_date)}
                            </span>
                          </div>
                          {currentSubscription.current_period_start && currentSubscription.current_period_end && (
                            <p className="text-xs theme-text-secondary mt-2">
                              Period: {formatDate(currentSubscription.current_period_start)} - {formatDate(currentSubscription.current_period_end)}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Payment Method */}
                      <div className="p-5 theme-card-bg theme-card-border rounded-xl border-2">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium theme-text-secondary uppercase tracking-wide">Payment Method</span>
                          <CreditCard size={18} className="theme-text-secondary" />
                        </div>
                        {currentSubscription.payment_method_last4 ? (
                          <>
                            <div className="flex items-center gap-3 mb-2">
                              {currentSubscription.payment_method_brand && (
                                <span className="text-xs uppercase font-semibold theme-text-secondary px-2 py-1 bg-[rgb(var(--card))] rounded">
                                  {currentSubscription.payment_method_brand}
                                </span>
                              )}
                            </div>
                            <div className="mb-2">
                              <span className="text-xl font-mono font-bold theme-text-primary tracking-wider">
                                •••• •••• •••• {currentSubscription.payment_method_last4}
                              </span>
                            </div>
                            <p className="text-xs theme-text-secondary mt-2">
                              Update in billing portal
                            </p>
                          </>
                        ) : permissionsSubscription && permissionsSubscription.plan_name !== 'launch' ? (
                          <div>
                            <p className="text-sm theme-text-secondary">
                              Payment method will appear after your first payment
                            </p>
                            <p className="text-xs theme-text-secondary mt-2">
                              Update in billing portal
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm theme-text-secondary">No payment method on file</p>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t theme-card-border">
                      <ModernButton
                        onClick={async () => {
                          setIsOpeningPortal(true);
                          try {
                            await openBillingPortal();
                          } catch (error: any) {
                            showErrorNotification(error?.message || 'Failed to open billing portal');
                            console.error('Error opening billing portal:', error);
                          } finally {
                            setIsOpeningPortal(false);
                          }
                        }}
                        disabled={isOpeningPortal}
                        className="flex-1 min-w-[160px] bg-gradient-to-r from-[#9c68f2] to-[#422ca5] hover:from-[#8a5ae8] hover:to-[#3a2580]"
                      >
                        {isOpeningPortal ? (
                          <>
                            <Loader2 size={18} className="mr-2 animate-spin" />
                            Opening Portal...
                          </>
                        ) : (
                          <>
                            <ExternalLink size={18} className="mr-2" />
                            Manage Billing
                          </>
                        )}
                      </ModernButton>
                      
                      {currentSubscription && !currentSubscription.cancel_at_period_end && (
                        <ModernButton
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to cancel your subscription? You will continue to have access until the end of your billing period.')) {
                              return;
                            }
                            setIsCancelling(true);
                            try {
                              await cancelSubscription();
                              await refreshSubscription();
                              showSuccessNotification('Subscription cancellation scheduled successfully');
                            } catch (error: any) {
                              showErrorNotification(error?.message || 'Failed to cancel subscription');
                              console.error('Error canceling subscription:', error);
                            } finally {
                              setIsCancelling(false);
                            }
                          }}
                          disabled={isCancelling}
                          variant="outline"
                          className="flex-1 min-w-[160px] border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500"
                        >
                          {isCancelling ? (
                            <>
                              <Loader2 size={18} className="mr-2 animate-spin" />
                              Cancelling...
                            </>
                          ) : (
                            <>
                              <X size={18} className="mr-2" />
                              Cancel Subscription
                            </>
                          )}
                        </ModernButton>
                      )}
                      
                      {/* Bouton Pause/Reprendre */}
                      {currentSubscription && (
                        currentSubscription.cancel_at_period_end ? (
                          <ModernButton
                            onClick={async () => {
                              try {
                                const result = await resumeSubscription();
                                if (result.success) {
                                  await refreshSubscription();
                                  showSuccessNotification('Subscription resumed successfully');
                                }
                              } catch (error: any) {
                                showErrorNotification(error?.message || 'Failed to resume subscription');
                                console.error('Error resuming subscription:', error);
                              }
                            }}
                            disabled={pauseLoading}
                            variant="outline"
                            className="flex-1 min-w-[160px] border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500"
                          >
                            {pauseLoading ? (
                              <>
                                <Loader2 size={18} className="mr-2 animate-spin" />
                                Resuming...
                              </>
                            ) : (
                              <>
                                <Clock size={18} className="mr-2" />
                                Resume Subscription
                              </>
                            )}
                          </ModernButton>
                        ) : (
                          <ModernButton
                            onClick={async () => {
                              try {
                                const result = await pauseSubscription();
                                if (result.success) {
                                  await refreshSubscription();
                                  showSuccessNotification('Subscription paused successfully');
                                }
                              } catch (error: any) {
                                showErrorNotification(error?.message || 'Failed to pause subscription');
                                console.error('Error pausing subscription:', error);
                              }
                            }}
                            disabled={pauseLoading}
                            variant="outline"
                            className="flex-1 min-w-[160px] border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 hover:border-yellow-500"
                          >
                            {pauseLoading ? (
                              <>
                                <Loader2 size={18} className="mr-2 animate-spin" />
                                Pausing...
                              </>
                            ) : (
                              <>
                                <Clock size={18} className="mr-2" />
                                Pause Subscription
                              </>
                            )}
                          </ModernButton>
                        )
                      )}

                      <ModernButton
                        onClick={() => navigate('/upgrade')}
                        variant="outline"
                        className="flex-1 min-w-[160px]"
                      >
                        <Crown size={18} className="mr-2" />
                        Change Plan
                      </ModernButton>
                    </div>
                  </div>
                </ModernCard>

                {import.meta.env.DEV && (
                  <ModernCard>
                    <div className="p-6">
                      <ModernButton
                        onClick={() => setShowDebugPanel(true)}
                        variant="outline"
                        className="w-full border-[rgb(var(--border))]"
                      >
                        Debug Subscription
                      </ModernButton>
                    </div>
                  </ModernCard>
                )}
              </>
            ) : (
              <>
                {/* Show current plan even if it's free/launch */}
                {permissionsSubscription && (
                  <ModernCard>
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-6">
                        <div>
                          <h3 className="text-xl font-semibold theme-text-primary mb-2">Current Plan</h3>
                          <p className="theme-text-secondary text-sm">Your current subscription plan</p>
                        </div>
                        <div className="w-12 h-12 bg-gradient-to-r from-[#9c68f2] to-[#422ca5] rounded-full flex items-center justify-center">
                          <Crown size={24} className="text-[rgb(var(--fg))]" />
                        </div>
                      </div>
                      
                      <div className="p-4 theme-card-bg theme-card-border rounded-lg mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium theme-text-secondary">Plan</span>
                          <span className="text-lg font-semibold theme-text-primary">
                            {permissionsSubscription.plan_display_name || permissionsSubscription.plan_name || 'Lunch'}
                          </span>
                        </div>
                        {permissionsSubscription.end_date && (
                          <p className="text-xs theme-text-secondary mt-1">
                            {permissionsSubscription.plan_name === 'launch' || permissionsSubscription.plan_name === 'free' 
                              ? 'Free plan - no expiration'
                              : `Expires: ${new Date(permissionsSubscription.end_date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}`}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-3 pt-4 border-t theme-card-border">
                        <ModernButton
                          onClick={() => navigate('/upgrade')}
                          className="flex-1 min-w-[140px] bg-gradient-to-r from-[#9c68f2] to-[#422ca5] hover:from-[#8a5ae8] hover:to-[#3a2580]"
                        >
                          <Crown size={16} className="mr-2" />
                          {permissionsSubscription.plan_name === 'launch' || permissionsSubscription.plan_name === 'free' 
                            ? 'Upgrade Plan'
                            : 'Change Plan'}
                        </ModernButton>
                      </div>
                    </div>
                  </ModernCard>
                )}
                
                {/* No subscription message if no data at all */}
                {!permissionsSubscription && (
                  <ModernCard>
                    <div className="p-6 text-center">
                      <div className="w-16 h-16 bg-gradient-to-r from-[#9c68f2] to-[#422ca5] rounded-full flex items-center justify-center mx-auto mb-4">
                        <Crown size={32} className="text-[rgb(var(--fg))]" />
                      </div>
                      <h3 className="text-2xl font-semibold theme-text-primary mb-2">No Active Subscription</h3>
                      <p className="theme-text-secondary mb-6">Upgrade to unlock premium features and boost your productivity</p>
                      <div className="flex justify-center gap-3">
                        <ModernButton 
                          onClick={() => navigate('/upgrade')}
                          className="bg-gradient-to-r from-[#9c68f2] to-[#422ca5] hover:from-[#8a5ae8] hover:to-[#3a2580]"
                        >
                          <Crown size={16} className="mr-2" />
                          View Plans & Upgrade
                        </ModernButton>
                      </div>
                    </div>
                  </ModernCard>
                )}
              </>
            )}
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <p className="theme-text-secondary">Category not found</p>
          </div>
        );
      }
    } catch (error) {
      console.error('Error rendering category content:', error);
      return (
        <div className="text-center py-12">
          <p className="theme-text-secondary text-red-400">Error loading category content</p>
        </div>
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen theme-layout-bg theme-text-primary p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">Settings</h1>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 size={48} className="mx-auto text-[#9c68f2] animate-spin mb-4" />
              <p className="theme-text-secondary">Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen theme-layout-bg theme-text-primary p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">Settings</h1>
          <div className="text-center py-12">
            <p className="theme-text-secondary">Please log in to access settings</p>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier que categories est valide avant de rendre
  if (!categories || !Array.isArray(categories) || categories.length === 0) {
    return (
      <div className="min-h-screen theme-layout-bg theme-text-primary p-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-2xl font-bold mb-6">Settings</h1>
          <div className="text-center py-12">
            <p className="theme-text-secondary">Error loading settings categories</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen theme-layout-bg theme-text-primary px-4 py-6 sm:px-6 lg:px-8 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Header with close button */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold theme-text-primary mb-1">Settings</h1>
            <p className="text-sm theme-text-secondary">Manage your account preferences and subscription</p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="p-2.5 rounded-xl theme-hover-button theme-text-secondary hover:theme-text-primary transition-all duration-200 hover:scale-105"
            title="Close settings"
          >
            <X size={22} />
          </button>
        </div>
        
        {/* Mobile category selector */}
        <div className="lg:hidden mb-6">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 px-1 scrollbar-hide">
            {categories.map((category) => {
              const IconComponent = category.icon;
              const isActive = activeCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex-shrink-0 inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gradient-to-r from-[#9c68f2] to-[#422ca5] text-white border-transparent shadow-lg shadow-[#9c68f2]/20'
                      : 'bg-[rgb(var(--card))] border-[rgb(var(--border))] text-[rgb(var(--muted))] hover:text-[rgb(var(--fg))] hover:border-[#9c68f2]/30'
                  }`}
                >
                  {IconComponent && typeof IconComponent === 'function' && (
                    <IconComponent size={16} className={isActive ? 'opacity-100' : 'opacity-70'} />
                  )}
                  <span>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,280px),1fr]">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-start-1">
            <ModernCard className="p-0 sticky top-6">
              <div className="p-5">
                <h3 className="text-xs font-bold theme-text-secondary uppercase tracking-wider mb-4 px-2">
                  Categories
                </h3>
                <nav className="space-y-1">
                  {categories.map((category) => {
                    const IconComponent = category.icon;
                    const isActive = activeCategory === category.id;
                    return (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-r from-[#9c68f2] to-[#422ca5] text-white shadow-lg shadow-[#9c68f2]/20'
                            : 'theme-text-secondary hover:theme-text-primary theme-hover-button hover:bg-[rgb(var(--card))]'
                        }`}
                      >
                        {IconComponent && typeof IconComponent === 'function' ? (
                          <IconComponent size={20} className={isActive ? 'opacity-100' : 'opacity-70'} />
                        ) : (
                          <div className="w-5 h-5" />
                        )}
                        <div className="flex-1">
                          <div className={`text-sm font-medium ${isActive ? 'text-white' : ''}`}>{category.name}</div>
                          <div className={`text-xs ${isActive ? 'text-white/80' : 'opacity-75'}`}>{category.description}</div>
                        </div>
                        <ChevronRight size={16} className={isActive ? 'opacity-70' : 'opacity-50'} />
                      </button>
                    );
                  })}
                </nav>
              </div>
            </ModernCard>
          </div>

          {/* Main Content */}
          <div className="lg:col-start-2">
            <ModernCard className="overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-center justify-between mb-8 pb-6 border-b theme-card-border">
                  <div>
                    <h2 className="text-2xl font-bold theme-text-primary mb-1">
                      {categories && Array.isArray(categories) ? (categories.find(c => c && c.id === activeCategory)?.name || 'Settings') : 'Settings'}
                    </h2>
                    <p className="text-sm theme-text-secondary">
                      {categories && Array.isArray(categories) ? (categories.find(c => c && c.id === activeCategory)?.description || '') : ''}
                    </p>
                  </div>
                </div>

                {renderCategoryContent()}

              {/* Subscription Debug Panel (dev only) */}
              {import.meta.env.DEV && (
                <SubscriptionDebugPanel 
                  isOpen={showDebugPanel} 
                  onClose={() => setShowDebugPanel(false)} 
                />
              )}

              {/* Save Button - Only show for profile, privacy, social categories */}
              {['profile', 'privacy', 'social'].includes(activeCategory) && (
                <div className="flex justify-end gap-3 mt-8 pt-6 border-t theme-card-border">
                  <ModernButton 
                    onClick={handleSaveProfile}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        Save
                      </>
                    )}
                  </ModernButton>
                </div>
              )}
            </div>
          </ModernCard>
        </div>
      </div>

      {/* Modales */}
      <SkillModal
        isOpen={isEditingSkill}
        onClose={() => {
          setIsEditingSkill(false);
          setEditingSkill(null);
        }}
        onSuccess={async () => {
          if (user) {
            try {
              const userSkills = await SkillsService.getUserSkills(user.id);
              setSkills(userSkills || []);
            } catch (error) {
              console.error('Error reloading skills:', error);
            }
          }
        }}
        skill={editingSkill}
      />

      <AwardModal
        isOpen={isEditingAward}
        onClose={() => {
          setIsEditingAward(false);
          setEditingAward(null);
        }}
        onSuccess={async () => {
          if (user) {
            try {
              const userAwards = await AwardsService.getUserAwards(user.id);
              setAwards(userAwards || []);
            } catch (error) {
              console.error('Error reloading awards:', error);
            }
          }
        }}
        award={editingAward}
      />
      </div>
    </div>
  );
};

const PageSettings: React.FC = () => {
  return (
    <SettingsErrorBoundary>
      <PageSettingsContent />
    </SettingsErrorBoundary>
  );
};

export default PageSettings;
